import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import type {
  OnyxMapRoutePointStatus,
  OnyxRouteLeg,
  OnyxScheduleCompliance,
} from '@onyx/angular';
import { MapHelper, OnyxRouteLegType } from '@onyx/angular';
import { OrderPointStatus } from '../../../../../dashboard/orders/common/enums/order-point-status';
import type { OrderPointFormGroup } from '../../../../../dashboard/orders/order-form/order-points-form/order-point-form/order-point-form.component';
import { RouteHelper } from '../../../../helpers/route.helper';
import type { RouteSubpoint } from '../../../../interfaces/route/route-subpoint';
import { TelematicsStore } from '../../../../stores/telematics.store';
import { RoutePointLegLineComponent } from '../route-point-leg-line/route-point-leg-line.component';
import { RoutePointLegSummaryComponent } from '../route-point-leg-summary/route-point-leg-summary.component';
import type { RoutePointRouteSummary } from '../route-point-route-summary/route-point-route-summary.component';
import { RoutePointComponent } from '../route-point.component';
import { RouteService } from './../../../../services/route.service';
import { RoutePointLegRideComponent } from './route-point-leg-ride/route-point-leg-ride.component';

@Component({
  selector: 'app-route-point-leg',
  imports: [
    RoutePointLegLineComponent,
    RoutePointLegSummaryComponent,
    NgTemplateOutlet,
    forwardRef(() => RoutePointComponent),
    RoutePointLegRideComponent,
  ],
  templateUrl: './route-point-leg.component.html',
  styleUrl: './route-point-leg.component.scss',
})
export class RoutePointLegComponent {
  protected readonly telematicsStore = inject(TelematicsStore);
  protected readonly routeService = inject(RouteService);

  public readonly leg = input.required<OnyxRouteLeg>();
  public readonly legs = input.required<OnyxRouteLeg[]>();
  public readonly index = input.required<number>();
  public readonly routeSummary =
    input.required<RoutePointRouteSummary | null>();
  public readonly subpointsRouteSummary =
    input.required<RoutePointRouteSummary | null>();
  public readonly subpoints = input.required<RouteSubpoint[]>();
  public readonly subpointsExpanded = input.required<boolean>();
  public readonly status = input.required<OnyxMapRoutePointStatus>();
  public readonly compliance = input.required<OnyxScheduleCompliance | null>();
  public readonly isPoint = input.required<boolean>();
  public readonly isFirstLeg = input.required<boolean>();
  public readonly isLastLeg = input.required<boolean>();
  public readonly isFirstSubpoint = input.required<boolean>();
  public readonly isCurrent = input.required<boolean>();
  public readonly isEmpty = input.required<boolean>();

  protected readonly OrderPointStatus = OrderPointStatus;
  protected readonly OnyxRouteLegType = OnyxRouteLegType;
  protected readonly Number = Number;

  protected readonly context = this.routeService.context;

  protected bordersExpanded = signal(false);

  protected telematicsItem = computed(() =>
    this.telematicsStore.getItem(this.routeService.vehicle()?.uuid),
  );
  protected hasSubpoints = computed(() => this.subpoints()?.length > 0);
  protected expandBottom = computed(
    () =>
      (this.isLastLeg() &&
        (this.subpointsExpanded() || !this.hasSubpoints())) ||
      (this.isPoint() &&
        this.isFirstLeg() &&
        this.hasSubpoints() &&
        !this.subpointsExpanded()),
  );
  protected showFirstEmptyRoute = computed(() =>
    this.routeService.showFirstEmptyRoute(),
  );

  protected trackSubpoint(
    subpoint: RouteSubpoint,
  ): number | OrderPointFormGroup | string {
    const { data } = subpoint;

    if ('form' in data) return data.form;
    if ('uuid' in data) return data.uuid;
    return RouteHelper.getUuid(data);
  }

  protected getPreviousLeg(currentLeg: OnyxRouteLeg): OnyxRouteLeg | null {
    const index = this.legs().findIndex(
      (leg) => MapHelper.getLegId(leg) === MapHelper.getLegId(currentLeg),
    );
    if (index <= 0) return null;

    return this.legs().at(index - 1) ?? null;
  }
}
